import "@/App.css";
import React, { useState, useEffect, useCallback, createContext, useContext } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { isAuthenticated, clearAuth } from "./lib/auth";
import { hasCompletedOnboarding, markOnboardingComplete, clearOnboardingFlag } from "./lib/onboarding";
import { authAPI } from "./lib/api";
import { Toaster } from "./components/ui/sonner";
import { I18nProvider } from "./lib/i18n";
import { initSentry, initPlausible, initClarity, wireConsentListener } from "./lib/analytics";
import { Loader2 } from "lucide-react";

// A13. Observability bootstrap. Runs at module load so even errors
// during React's initial render get caught (if Sentry's consented +
// configured). Plausible loads unconditionally if its domain is set
// since it's cookie-free; Sentry + Clarity wait for analytics consent.
initPlausible();
initSentry();
initClarity();
wireConsentListener();

import LandingPage from "./pages/LandingPage";
import AuthPage from "./pages/AuthPage";
import Dashboard from "./pages/Dashboard";
import PropertiesPage from "./pages/PropertiesPage";
import PropertyDetailPage from "./pages/PropertyDetailPage";
import ProfilePage from "./pages/ProfilePage";
import VideoGalleryPage from "./pages/VideoGalleryPage";
import SocialMediaPage from "./pages/SocialMediaPage";
import JustimmoPage from "./pages/JustimmoPage";
import ImportsPage from "./pages/ImportsPage";
import StudioTeaserPage from "./pages/StudioTeaserPage";
import PricingPage from "./pages/PricingPage";
import SubscriptionSuccessPage from "./pages/SubscriptionSuccessPage";
import AutoPostSettingsPage from "./pages/AutoPostSettingsPage";
import OnboardingPage from "./pages/OnboardingPage";
import OnboardingWizard from "./components/onboarding/OnboardingWizard";
import TeamPage from "./pages/TeamPage";
import TeamInvitePage from "./pages/TeamInvitePage";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import PreviewPage from "./pages/PreviewPage";
import DemoSharePage from "./pages/DemoSharePage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import ImpressumPage from "./pages/LegalPages/ImpressumPage";
import DatenschutzPage from "./pages/LegalPages/DatenschutzPage";
import AGBPage from "./pages/LegalPages/AGBPage";
import WiderrufsrechtPage from "./pages/LegalPages/WiderrufsrechtPage";
import FAQPage from "./pages/FAQPage";
import CookieBanner from "./components/CookieBanner";

// Auth context: validates token on startup before rendering protected routes.
// Consumers can also call `markAuthenticated(bool)` after login/logout to push
// state changes into the context without waiting for the next mount. Fixes
// the "stuck on /auth after login" bug caused by the mount-only validateToken.
const AuthContext = createContext({
  authReady: false,
  authenticated: false,
  markAuthenticated: () => {},
});
export const useAuth = () => useContext(AuthContext);

function AuthProvider({ children }) {
  const [authReady, setAuthReady] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);

  useEffect(() => {
    const validateToken = async () => {
      if (!isAuthenticated()) {
        setAuthenticated(false);
        setAuthReady(true);
        return;
      }
      try {
        const me = await authAPI.getMe();
        // Sync onboarding status from backend (server is source of truth;
        // localStorage is just a no-network cache for OnboardingGuard).
        if (me.data.onboarding_completed) {
          markOnboardingComplete();
        } else {
          // Server says no. Drop any stale local cache so a user
          // whose record was reset (rare, support intervention) sees
          // the wizard again.
          clearOnboardingFlag();
        }
        setAuthenticated(true);
      } catch {
        // Token is invalid/expired. Clear everything
        clearAuth();
        setAuthenticated(false);
      }
      setAuthReady(true);
    };
    validateToken();
  }, []);

  // Stable so consumers can pull it into effect deps without re-firing.
  const markAuthenticated = useCallback((value) => {
    setAuthenticated(Boolean(value));
  }, []);

  return (
    <AuthContext.Provider value={{ authReady, authenticated, markAuthenticated }}>
      {children}
    </AuthContext.Provider>
  );
}

// v1.8. Prefix-URL funnel: `real-content.at/<willhaben-url>` should land
// in the public demo with the listing prefilled and the render already
// started — a marketable trick ("prefix any Willhaben listing with
// real-content.at/"). nginx serves index.html for every unknown path
// (SPA fallback), so the raw willhaben URL arrives here as the pathname.
// Tolerated shapes: full scheme (https://www.willhaben.at/...), a
// collapsed single slash (https:/www... — some chat apps and proxies
// eat the double slash), scheme-less (www.willhaben.at/...), and
// percent-encoded variants. Everything else falls back to the landing.
function extractWillhabenUrl(raw) {
  let s = raw.startsWith('/') ? raw.slice(1) : raw;
  try {
    s = decodeURIComponent(s);
  } catch {
    // malformed escape sequence — keep the raw string
  }
  s = s.replace(/^https?:\/{1,2}/i, '');
  const m = s.match(/^(?:www\.)?willhaben\.at\/(iad\/immobilien\/d\/.+)$/i);
  if (!m) return null;
  return `https://www.willhaben.at/${m[1]}`;
}

function PrefixCatchAll() {
  // window.location, not the router location: the browser preserves the
  // literal `//` after the scheme, and the listing's own query params
  // live in `search` and belong to the willhaben URL.
  const raw = window.location.pathname + window.location.search;
  const target = extractWillhabenUrl(raw);
  return <Navigate to={target ? `/?inserat=${encodeURIComponent(target)}` : '/'} replace />;
}

const ProtectedRoute = ({ children }) => {
  const { authReady, authenticated } = useAuth();
  if (!authReady) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f7f3ea]">
        <Loader2 className="w-8 h-8 text-[#1f4d3a] animate-spin" />
      </div>
    );
  }
  return authenticated ? children : <Navigate to="/auth" />;
};

// Onboarding guard: redirect new users to /onboarding. Versioned via
// hasCompletedOnboarding() so a future ONBOARDING_VERSION bump
// re-prompts existing users through any new steps.
const OnboardingGuard = ({ children }) => {
  if (!hasCompletedOnboarding()) {
    return <Navigate to="/onboarding" />;
  }
  return children;
};

function App() {
  return (
    <I18nProvider>
      <div className="App">
        <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/auth" element={<AuthPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            {/* Email-verify landing. Public route. Token in URL is the auth. */}
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            {/* Password-reset landing. Public route. The raw token
                from the email is in `?token=...`; backend matches it
                against the SHA-256 hash stored server-side. */}
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            {/* Team-invite landing. Public route. JWT in URL is the
                auth grant for the accept call; the page itself bounces
                logged-out users through /auth?redirect=/team/invite. */}
            <Route path="/team/invite" element={<TeamInvitePage />} />
            {/* Auto-post preview. Public route. Token in URL is the auth.
                User lands here from the preview email's three buttons. */}
            <Route path="/preview" element={<PreviewPage />} />
            {/* Viral demo share page. Public route. The token in
                the URL is the access. Visitor lands here after
                pasting a Willhaben URL on the landing page, or via
                a shared link. CTAs route to /auth?demo=<token>
                so AuthPage can claim the render after signup. */}
            <Route path="/demo/:token" element={<DemoSharePage />} />
            {/* Legal pages. Public routes (must be reachable without
                login since the register form links to AGB + Datenschutz). */}
            <Route path="/impressum" element={<ImpressumPage />} />
            <Route path="/datenschutz" element={<DatenschutzPage />} />
            <Route path="/agb" element={<AGBPage />} />
            <Route path="/widerrufsrecht" element={<WiderrufsrechtPage />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/subscription/success" element={
              <ProtectedRoute>
                <SubscriptionSuccessPage />
              </ProtectedRoute>
            } />
            <Route path="/onboarding" element={
              <ProtectedRoute>
                <OnboardingPage />
              </ProtectedRoute>
            } />
            {/* /setup. Full 7-step setup wizard for *paid* users only.
                Free-tier users get the lightweight FirstRenderPage at
                /onboarding instead; this route is what newly-upgraded
                Solo/Team users land on after Stripe success so every
                feature their subscription unlocks (Justimmo, socials,
                voice, music, auto-post schedule) gets configured up
                front. Without this guided path the user can flip on
                auto-post with no socials/branding and ship broken
                videos before they've ever seen the settings. */}
            <Route path="/setup" element={
              <ProtectedRoute>
                <OnboardingWizard />
              </ProtectedRoute>
            } />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <OnboardingGuard>
                    <Dashboard />
                  </OnboardingGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <OnboardingGuard>
                    <ProfilePage />
                  </OnboardingGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/gallery"
              element={
                <ProtectedRoute>
                  <OnboardingGuard>
                    <VideoGalleryPage />
                  </OnboardingGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/properties"
              element={
                <ProtectedRoute>
                  <OnboardingGuard>
                    <PropertiesPage />
                  </OnboardingGuard>
                </ProtectedRoute>
              }
            />
            {/* /properties/new is now a modal. Keep the URL alive but
                bounce back to the list (where the modal lives). Avoids
                breaking deep-links from old emails / docs. */}
            <Route path="/properties/new" element={<Navigate to="/properties" replace />} />
            <Route
              path="/properties/:id"
              element={
                <ProtectedRoute>
                  <OnboardingGuard>
                    <PropertyDetailPage />
                  </OnboardingGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/social"
              element={
                <ProtectedRoute>
                  <OnboardingGuard>
                    <SocialMediaPage />
                  </OnboardingGuard>
                </ProtectedRoute>
              }
            />
            {/* Redirect old routes */}
            <Route path="/justimmo" element={
              <ProtectedRoute>
                <OnboardingGuard>
                  <JustimmoPage />
                </OnboardingGuard>
              </ProtectedRoute>
            } />
            {/* Imports hub. Single canonical place to manage Justimmo +
                Willhaben source connections. Replaces the old "click
                Justimmo in nav" path with a unified surface that
                surfaces both integrations + a duplicate-import warning
                when both are connected. */}
            <Route path="/imports" element={
              <ProtectedRoute>
                <OnboardingGuard>
                  <ImportsPage />
                </OnboardingGuard>
              </ProtectedRoute>
            } />
            <Route path="/studio" element={
              <ProtectedRoute>
                <OnboardingGuard>
                  <StudioTeaserPage />
                </OnboardingGuard>
              </ProtectedRoute>
            } />
            <Route
              path="/auto-post"
              element={
                <ProtectedRoute>
                  <OnboardingGuard>
                    <AutoPostSettingsPage />
                  </OnboardingGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/team"
              element={
                <ProtectedRoute>
                  <OnboardingGuard>
                    <TeamPage />
                  </OnboardingGuard>
                </ProtectedRoute>
              }
            />
            {/* Catch-all. Willhaben prefix-URLs → demo funnel; anything
                else → landing (previously unmatched paths rendered a
                blank page). MUST stay the last route. */}
            <Route path="*" element={<PrefixCatchAll />} />
          </Routes>
          {/* Cookie consent banner. Global, sits above everything,
              hides itself once the user has decided. */}
          <CookieBanner />
        </AuthProvider>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </div>
    </I18nProvider>
  );
}

export default App;
